import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type {
  AccessApprovalCard,
  AccessMatrixCell,
  AccessPeople,
  AccessPeopleFilter,
  AccessPerson,
  AccessPersonPage,
  AccessPersonRow,
  AccessPersonStatus,
  AccessWarning,
  ApiFailure,
} from '../api/types'
import type { Api } from '../api'
import { Badge, Button, Card, EmptyState, Loading, Notice, PageTabs, PersonCell, SearchInput, SearchSelect } from '../components/ui'
import { HierarchyTab, HistoryTab, SignInLink } from './AccessPersonTabs'
import { ExtraScopesTab, MainScopeTab } from './AccessPersonEdit'
import { DataGrid, type Column } from '../components/DataGrid'
import { GridChromeSlot } from '../components/gridLayout'
import { FailureState } from '../components/FailureState'
import { ConfirmDialog } from '../components/ConfirmDialog'
import {
  ApprovalConsequences, HIERARCHY, SEVERITY, STATUS, STATUS_ORDER, SYSTEM_ROLE, Fact, FilterSlot, Initials, Meta, Panel, PlaceChip, SeverityTag, StateDot, StatusBadge, ToggleChip, Verdict,
  ago, approveLabel, day, dayTime, entryReason, extraScopesOf, failureOf, listWords, messageOf, roleLabel,
} from './accessWords'

/**
 * ACCESS › PEOPLE, under the new permission model (05 sections 7.3 and 7.4).
 *
 * Everyone Mindzer sends, one row each, and a page per person that answers, in
 * order: can they get in; what do they see, and why; what is wrong with their
 * data. The server states every fact here -- the badge, the entry checks, each
 * cell's source, each warning's sentence -- and this screen only shows them, so
 * the list, the person page and the approval card can never disagree.
 *
 * The list stays mounted behind the person page, so "← People" comes back with the
 * search, the filters and the scroll as they were. A person opened from another's
 * Hierarchy tab goes on a trail, and Back returns along it.
 *
 * Laid out again 27 Oct 2026 (owner: "the design is not acceptable"): the filters
 * named above their controls, the on/off filters as chips, one results line with
 * the count per badge, and every block of the person page on its own card.
 */
export function AccessPeopleTab({ api, openRequest, onOpened, onChanged, actor = null, onLoginAs }: {
  api: Api
  /** Another tab asked for a person ("Open their page →" on an approval card), on a tab of their page if it says. */
  openRequest?: { id: number; seq: number; section?: PersonSection } | null
  /** The request was used: it must not reopen the person on the next visit. */
  onOpened?: () => void
  /** A person's approval or settings changed: the tab counts are read again. */
  onChanged?: () => void
  /** Who is signed in, for "This is you." */
  actor?: string | null
  /** Start Login As; absent for an admin who may not act (D7). */
  onLoginAs?: (oid: string) => void
}) {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<AccessPeopleFilter>({})
  const [data, setData] = useState<AccessPeople | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [reload, setReload] = useState(0)
  // The people opened, in order: the last is on screen.
  const [trail, setTrail] = useState<{ id: number; name: string | null; section?: PersonSection }[]>([])
  // Companies and roles seen so far: a filtered list must not take its own filter's options away.
  const [companies, setCompanies] = useState<string[]>([])
  const [roles, setRoles] = useState<{ id: number; label: string }[]>([])
  const [chromeSlot, setChromeSlot] = useState<HTMLElement | null>(null)
  const open = trail.length ? trail[trail.length - 1] : null

  useEffect(() => {
    if (!openRequest) return
    setTrail([{ id: openRequest.id, name: null, section: openRequest.section }])
    onOpened?.()
  }, [openRequest, onOpened])

  // The search is the server's, a moment after typing stops.
  useEffect(() => {
    // Unchanged, the filter keeps its identity: "Clear all filters" must not read the list twice.
    const h = setTimeout(() => setFilter((f) => (f.search === (search.trim() || undefined) ? f : { ...f, search: search.trim() || undefined })), 250)
    return () => clearTimeout(h)
  }, [search])

  // The companies and roles a page names, for the menus.
  const learn = useCallback((d: AccessPeople) => {
    setCompanies((seen) => [...new Set([...seen, ...d.people.map((p) => p.compId).filter((c): c is string => !!c)])].sort())
    setRoles((seen) => {
      const byId = new Map(seen.map((r) => [r.id, r]))
      for (const p of d.people) if (p.roleId !== null) byId.set(p.roleId, { id: p.roleId, label: roleLabel(p) })
      return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label))
    })
  }, [])

  // Which list is on screen: a page asked for under an older filter is dropped.
  const listGen = useRef(0)
  // Pages after the first, added below it (review R-20): the list is in name order.
  // ready: page 1 of the current filter has landed (no next page is asked before it);
  // full: the last page came back whole, so another may follow.
  const [more, setMore] = useState<{ page: number; ready: boolean; full: boolean; busy: boolean; failure: string | null }>(
    { page: 1, ready: false, full: false, busy: false, failure: null })

  useEffect(() => {
    let cancelled = false
    const gen = ++listGen.current
    setFailure(null)
    setMore({ page: 1, ready: false, full: false, busy: false, failure: null })
    api
      .accessPeople(filter)
      .then((d) => {
        if (cancelled || gen !== listGen.current) return
        setData(d)
        learn(d)
        setMore({ page: 1, ready: true, full: d.people.length >= PEOPLE_PAGE, busy: false, failure: null })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setData(null)
        setFailure(failureOf(e))
      })
    return () => {
      cancelled = true
    }
  }, [api, filter, reload, learn])

  const showNext = () => {
    if (!data || more.busy || !more.ready || !more.full) return
    const gen = listGen.current
    const page = more.page + 1
    setMore((m) => ({ ...m, busy: true, failure: null }))
    api
      .accessPeople({ ...filter, page })
      .then((d) => {
        if (gen !== listGen.current) return
        // An update between two pages can move a person across the break: never list one twice.
        setData((cur) => {
          if (!cur) return cur
          const have = new Set(cur.people.map((p) => p.mindzerUserId))
          return { ...d, people: [...cur.people, ...d.people.filter((p) => !have.has(p.mindzerUserId))] }
        })
        learn(d)
        setMore({ page, ready: true, full: d.people.length >= PEOPLE_PAGE, busy: false, failure: null })
      })
      .catch((e: unknown) => {
        if (gen === listGen.current) setMore((m) => ({ ...m, busy: false, failure: messageOf(e) }))
      })
  }

  // When Mindzer last sent a good update, beside the active count (05 7.3; review R-21).
  // A decoration: without it the count still stands, so a failure is not shown twice.
  const [updatedUtc, setUpdatedUtc] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    api
      .accessSync()
      .then((s) => {
        if (!cancelled) setUpdatedUtc(s.status?.lastGoodUtc ?? null)
      })
      .catch(() => {
        if (!cancelled) setUpdatedUtc(null)
      })
    return () => {
      cancelled = true
    }
  }, [api, reload])

  const columns = useMemo<Column<AccessPersonRow>[]>(
    () => [
      {
        key: 'person',
        header: 'User',
        align: 'left',
        stickyLeft: true,
        sortValue: (r) => r.displayName ?? r.email ?? '',
        render: (r) => <PersonCell name={r.displayName} email={r.email ?? `Mindzer user ${r.mindzerUserId}`} />,
      },
      { key: 'compId', header: 'Company', align: 'left', sortValue: (r) => r.compId ?? '', render: (r) => r.compId ?? '—' },
      {
        key: 'role',
        header: 'HR Role',
        align: 'left',
        sortValue: (r) => (r.roleName ? roleLabel(r) : ''),
        render: (r) => (r.roleName ? roleLabel(r) : <span className="text-(--color-ink-4)">No HR Role</span>),
      },
      {
        key: 'status',
        header: 'Access status',
        align: 'left',
        sortValue: (r) => STATUS_ORDER.indexOf(r.status),
        render: (r) => <StatusBadge status={r.status} />,
      },
      {
        key: 'approval',
        header: 'Approval',
        align: 'left',
        sortValue: (r) => r.decidedUtc ?? '',
        render: (r) => approvalWords(r.status, r.approval, r.decidedUtc, r.approvalInForce),
      },
      {
        key: 'warnings',
        header: 'Warnings',
        align: 'right',
        value: (r) => r.warningCount,
        render: (r) => (r.warningCount > 0
          ? <span className="font-semibold text-(--color-warn)">⚠ {r.warningCount}</span>
          : <span className="text-(--color-ink-4)">—</span>),
      },
      {
        key: 'lastSignIn',
        header: 'Last signed in',
        align: 'left',
        sortValue: (r) => r.lastSignInUtc ?? '',
        render: (r) => (r.lastSignInUtc ? ago(r.lastSignInUtc) : <span className="text-(--color-ink-4)">Never</span>),
      },
      // In the Columns panel, off until asked for (05 7.3; review R-21, 27 Oct 2026).
      {
        key: 'personalChanges',
        header: 'Overrides',
        align: 'right',
        hiddenByDefault: true,
        value: (r) => r.personalChanges,
        render: (r) => (r.personalChanges > 0 ? r.personalChanges : <span className="text-(--color-ink-4)">—</span>),
      },
      {
        key: 'extraScopes',
        header: 'Additional scopes',
        align: 'right',
        hiddenByDefault: true,
        value: (r) => r.extraScopes,
        render: (r) => (r.extraScopes > 0 ? r.extraScopes : <span className="text-(--color-ink-4)">—</span>),
      },
      {
        key: 'hierarchy',
        header: 'Hierarchy',
        align: 'left',
        hiddenByDefault: true,
        sortValue: (r) => r.hierarchyMode,
        render: (r) => HIERARCHY[r.hierarchyMode] ?? '—',
      },
      {
        key: 'phone',
        header: 'Phone',
        align: 'left',
        hiddenByDefault: true,
        sortValue: (r) => (r.mobileAllowed ? 1 : 0),
        render: (r) => (r.mobileAllowed ? 'Allowed' : 'Not allowed'),
      },
    ],
    [],
  )

  const setFlag = (key: 'warnings' | 'personal' | 'left', on: boolean) => setFilter((f) => ({ ...f, [key]: on || undefined }))
  const narrowed = !!(filter.search || filter.status || filter.company || filter.role !== undefined || filter.warnings || filter.personal)
  const clearAll = () => {
    setSearch('')
    setFilter({})
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {open !== null && (
        <PersonPage
          key={`${open.id}-${trail.length}`}
          api={api}
          id={open.id}
          firstSection={open.section}
          backLabel={trail.length > 1 ? `Back to ${trail[trail.length - 2].name ?? 'the last user'}` : 'Users'}
          onBack={() => setTrail((t) => t.slice(0, -1))}
          onOpenPerson={(id, fromName) => setTrail((t) => [
            ...t.slice(0, -1), { ...t[t.length - 1], name: fromName, section: 'hierarchy' }, { id, name: null, section: 'hierarchy' }])}
          onChanged={() => {
            setReload((n) => n + 1)
            onChanged?.()
          }}
          actor={actor}
          onLoginAs={onLoginAs}
        />
      )}

      <Card className={`flex min-h-0 flex-1 flex-col ${open !== null ? 'hidden' : ''}`}>
        <div className="flex shrink-0 flex-col gap-3 border-b border-(--color-line) px-5 py-(--band-y)">
          <div className="flex flex-wrap items-end gap-x-3 gap-y-3">
            <div className="flex min-w-[240px] flex-[1.6] flex-col gap-1 @6xl:max-w-[380px]">
              <span className="eyebrow">Find a user</span>
              <SearchInput label="Search users" placeholder="Name, email, company or HR Role…" value={search} onChange={setSearch} />
            </div>
            <FilterSlot label="Access status">
              <SearchSelect
                label="Access status"
                value={filter.status ?? ''}
                onChange={(v) => setFilter((f) => ({ ...f, status: (v || undefined) as AccessPersonStatus | undefined }))}
                options={[{ value: '', label: 'Anyone' }, ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS[s].label }))]}
              />
            </FilterSlot>
            <FilterSlot label="Company">
              <SearchSelect
                label="Company"
                value={filter.company ?? ''}
                onChange={(v) => setFilter((f) => ({ ...f, company: v || undefined }))}
                options={[{ value: '', label: 'All companies' }, ...companies.map((c) => ({ value: c, label: c }))]}
              />
            </FilterSlot>
            <FilterSlot label="HR Role" wide>
              <SearchSelect
                label="HR Role"
                keepCase
                value={filter.role === undefined ? '' : String(filter.role)}
                onChange={(v) => setFilter((f) => ({ ...f, role: v ? Number(v) : undefined }))}
                options={[{ value: '', label: 'All HR Roles' }, ...roles.map((r) => ({ value: String(r.id), label: r.label }))]}
              />
            </FilterSlot>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="eyebrow mr-1">Show only</span>
            <ToggleChip on={!!filter.warnings} onChange={(on) => setFlag('warnings', on)}>With warnings</ToggleChip>
            <ToggleChip on={!!filter.personal} onChange={(on) => setFlag('personal', on)}>With overrides or additional scopes</ToggleChip>
            <span aria-hidden className="mx-1 h-4 w-px bg-(--color-line-strong)" />
            <ToggleChip on={!!filter.left} onChange={(on) => setFlag('left', on)}>Include users who left</ToggleChip>
            <span className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1">
              {!failure && <ListedCount data={data} updatedUtc={updatedUtc} more={more} onMore={showNext}
                onReread={() => setReload((n) => n + 1)} />}
              {(narrowed || filter.left) && (
                <button type="button" onClick={clearAll} className="rounded px-1 text-(length:--text-small) font-medium text-(--color-navy-700) hover:underline">
                  Clear all filters
                </button>
              )}
            </span>
          </div>
        </div>

        {/* Only over a grid: while reading, on a failure or with nobody listed it would be an empty bar. */}
        {!failure && !!data?.people.length && (
          <StatusLine data={data} status={filter.status} onStatus={(s) => setFilter((f) => ({ ...f, status: s }))} slotRef={setChromeSlot} />
        )}

        {failure ? (
          <FailureState failure={failure} onRetry={() => setReload((n) => n + 1)} />
        ) : data === null ? (
          <Loading label="Reading users from Mindzer…" />
        ) : data.people.length === 0 ? (
          <EmptyState
            title={narrowed ? 'Nobody matches these filters.' : 'Mindzer has sent nobody yet.'}
            detail={narrowed ? 'Clear the search or a filter to see more users.' : 'Users appear after the first hourly Mindzer update. See Mindzer sync report.'}
          />
        ) : (
          // The grid's Columns control goes on the status line, not on a strip of its own.
          <GridChromeSlot.Provider value={chromeSlot}>
            {/* Up to 2,000 people: only the rows in view are drawn (owner, 29 Oct 2026: "lagging"). */}
            <DataGrid fill virtual gridId="access-people" columns={columns} rows={data.people}
              onRowClick={(r) => setTrail([{ id: r.mindzerUserId, name: r.displayName }])} />
          </GridChromeSlot.Provider>
        )}
      </Card>
    </div>
  )
}

/** The server's page of people (PeopleAdminEndpoints.ListPage). */
const PEOPLE_PAGE = 2000

const TONE_DOT: Record<string, string> = {
  good: 'bg-(--color-good)', warn: 'bg-(--color-warn)', bad: 'bg-(--color-overdue)', privileged: 'bg-(--color-privileged)', neutral: 'bg-(--color-ink-4)',
}

/**
 * How many are listed, and how many Mindzer has (owner, 27 Oct 2026: the two count
 * boxes were "not tidy"). Until the list is read it says so: a count of 0 would be a claim.
 */
function ListedCount({ data, updatedUtc, more, onMore, onReread }: {
  data: AccessPeople | null
  updatedUtc: string | null
  more: { ready: boolean; full: boolean; busy: boolean; failure: string | null }
  onMore: () => void
  /** Read the list again from page 1. */
  onReread: () => void
}) {
  const listed = data?.people.length ?? null
  // How many match (17g); an API before it sends none, and what is listed is all it says.
  const matched = data?.listed ?? null
  const rest = listed !== null && matched !== null ? matched - listed : 0
  return (
    <span className="text-(length:--text-small) text-(--color-ink-3)">
      {listed === null ? 'Reading users…' : (
        <>
          <span className="text-(length:--text-base) font-semibold text-(--color-ink) tabular-nums">{listed.toLocaleString('en-GB')}</span>{' '}
          {rest > 0 && matched !== null
            ? <>of {matched.toLocaleString('en-GB')} users listed</>
            : <>{listed === 1 ? 'user' : 'users'} listed</>}
        </>
      )}
      {data?.activeTotal != null && <> · Mindzer has {data.activeTotal.toLocaleString('en-GB')} active</>}
      {data?.activeTotal != null && updatedUtc && <> · updated {dayTime(updatedUtc)}</>}
      {rest > 0 && more.ready && more.full && (
        <button
          type="button"
          onClick={onMore}
          disabled={more.busy}
          title="Sorting and the Columns panel work on the users listed."
          className="ml-2 rounded px-1 font-medium text-(--color-navy-700) hover:underline disabled:opacity-60"
        >
          {more.busy ? 'Reading…' : `Show the next ${Math.min(rest, PEOPLE_PAGE).toLocaleString('en-GB')}`}
        </button>
      )}
      {/* The last page came back short of the count: an update moved people across a
          page while it was read. Reading again lists everyone once. */}
      {rest > 0 && more.ready && !more.full && (
        <button type="button" onClick={onReread}
          className="ml-2 rounded px-1 font-medium text-(--color-navy-700) hover:underline">
          The list changed while it was read: read it again
        </button>
      )}
      {more.failure && <span className="font-medium text-(--color-overdue)"> · {more.failure}</span>}
    </span>
  )
}

/**
 * How many of the listed carry each badge, each count a click that shows only
 * them, and the grid's Columns control at the end of the line.
 */
function StatusLine({ data, status, onStatus, slotRef }: {
  data: AccessPeople | null
  status?: AccessPersonStatus
  onStatus: (s: AccessPersonStatus | undefined) => void
  slotRef: (el: HTMLElement | null) => void
}) {
  const counts = useMemo(() => {
    const m = new Map<AccessPersonStatus, number>()
    for (const p of data?.people ?? []) m.set(p.status, (m.get(p.status) ?? 0) + 1)
    return STATUS_ORDER.filter((s) => m.has(s)).map((s) => [s, m.get(s)!] as const)
  }, [data])
  return (
    <div className="flex shrink-0 items-center gap-3 border-b border-(--color-line) bg-(--color-surface-2) px-5 py-1.5">
      <div role="group" aria-label="How many can enter, by badge" className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        {counts.map(([s, n]) => {
          const on = status === s
          return (
            <button
              key={s}
              type="button"
              aria-pressed={on}
              onClick={() => onStatus(on ? undefined : s)}
              title={on ? 'Show every badge again' : `Show only: ${STATUS[s].label}`}
              className={`inline-flex h-6 items-center gap-1 rounded-full border px-2 text-(length:--text-small) transition-colors ${
                on
                  ? 'border-(--color-navy-700) bg-(--color-navy-50) text-(--color-ink)'
                  : 'border-(--color-line) bg-(--color-surface) text-(--color-ink-2) hover:border-(--color-navy-300) hover:bg-(--color-navy-50)'
              }`}
            >
              <span aria-hidden className={`h-2 w-2 rounded-full ${TONE_DOT[STATUS[s].tone]}`} />
              {STATUS[s].label}
              <span className="font-semibold text-(--color-ink) tabular-nums">{n.toLocaleString('en-GB')}</span>
              {on && <span aria-hidden className="text-(--color-ink-3)">✕</span>}
            </button>
          )
        })}
      </div>
      <span ref={slotRef} className="flex shrink-0 items-center" />
    </div>
  )
}

/* =========================================================================
   THE PERSON'S PAGE (7.4)
   ========================================================================= */

export type PersonSection = 'see' | 'main' | 'extras' | 'hierarchy' | 'history'

function PersonPage({ api, id, firstSection, backLabel, onBack, onOpenPerson, onChanged, actor, onLoginAs }: {
  api: Api
  id: number
  firstSection?: PersonSection
  backLabel: string
  onBack: () => void
  /** Open another person (a manager or staff member), keeping this one to come back to. */
  onOpenPerson: (id: number, thisName: string | null) => void
  onChanged: () => void
  actor: string | null
  onLoginAs?: (oid: string) => void
}) {
  const [page, setPage] = useState<AccessPersonPage | null>(null)
  // The approval card, for its warnings key: an approval quotes exactly the warnings shown.
  const [card, setCard] = useState<AccessApprovalCard | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [reload, setReload] = useState(0)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)
  const [withdrawing, setWithdrawing] = useState(false)
  const [reason, setReason] = useState('')
  const [section, setSection] = useState<PersonSection>(firstSection ?? 'see')

  useEffect(() => {
    let cancelled = false
    setPage(null)
    setCard(null)
    setFailure(null)
    Promise.all([api.accessPerson(id), api.accessApprovalCard(id)])
      .then(([p, c]) => {
        if (cancelled) return
        setPage(p)
        setCard(c)
      })
      .catch((e: unknown) => !cancelled && setFailure(failureOf(e)))
    return () => {
      cancelled = true
    }
  }, [api, id, reload])

  const p = page?.person ?? null
  // A system role has no main scope or additional scope tab: a page opened on one shows What they see (review of 18d).
  const shown: PersonSection = p?.systemRole && (section === 'main' || section === 'extras') ? 'see' : section
  // A change saved from a tab: said here, on the page, which outlives the reload
  // that re-reads the person (the tab itself is re-created by it).
  const saved = (text: string) => {
    setNotice({ tone: 'good', text })
    setReload((n) => n + 1)
    onChanged()
  }
  // The warnings shown ARE the card's, read with the key an approval quotes: an
  // approval records exactly what was on screen (03 r27; review, 26 Oct 2026).
  const warnings = card?.warnings ?? page?.warnings ?? []
  // What may be done follows the server, not this screen's reading of fields: the badge
  // says who may be offered an approval, Approval_In_Force (17k) whether one already
  // counts. The badge alone offered Approve to someone approved whose HR role was not
  // set up, since "role not set up" comes first in its order, and no take-back (owner,
  // 28 Oct 2026). From a database before 17k, the badges as before.
  const canApprove = !!p && !!card?.person && APPROVABLE.includes(p.status) && p.approvalInForce !== true
  // Not for a placeholder account: the server refuses every decision on one (51609).
  const canWithdraw = !!p && p.status !== 'PLACEHOLDER'
    && (p.approvalInForce ?? (p.status === 'CAN_ENTER' || p.status === 'NO_SIGN_IN'))
  // What a take-back does now, as on Approvals › Approved: it shuts them out; a system
  // role lets them in whatever it says (the badge, not the role: a role whose data
  // bypass is off lets nobody in); or something else keeps them out already.
  const effect = !p || p.status === 'CAN_ENTER' || p.status === 'NO_SIGN_IN' ? 'entry' : p.status === 'SEES_ALL' ? 'system' : 'kept'

  const approve = () => {
    const key = card?.person?.warningsKey
    if (!key || busy) return
    setBusy(true)
    setNotice(null)
    api
      .accessApprove(id, key)
      .then((r) => {
        setNotice({ tone: 'good', text: r.built === false
          ? 'Approved. Their access is built at the next hourly update.'
          : 'Approved. Their access follows at once.' })
        setReload((n) => n + 1)
        onChanged()
      })
      .catch((e: unknown) => {
        const f = failureOf(e)
        setNotice({ tone: 'bad', text: messageOf(e) })
        // Their warnings changed since the page was read: show the new ones.
        if (f.kind === 'error' && f.reason === 'WARNINGS_CHANGED') setReload((n) => n + 1)
      })
      .finally(() => {
        setBusy(false)
        setConfirmApprove(false)
      })
  }
  // Approve asks first, as on Approvals (owner, 27 Oct 2026).
  const [confirmApprove, setConfirmApprove] = useState(false)

  const withdraw = () => {
    if (!reason.trim() || busy) return
    setBusy(true)
    setNotice(null)
    api
      .accessWithdraw(id, reason.trim())
      .then(() => {
        setNotice({ tone: 'good', text: `Approval taken back. ${effect === 'entry' ? 'They are waiting again.'
          : effect === 'system' ? 'Their system role still lets them in.' : 'They need approving again before they can enter.'}` })
        setWithdrawing(false)
        setReason('')
        setReload((n) => n + 1)
        onChanged()
      })
      .catch((e: unknown) => setNotice({ tone: 'bad', text: messageOf(e) }))
      .finally(() => setBusy(false))
  }

  const name = p ? p.displayName ?? `Mindzer user ${p.mindzerUserId}` : null

  return (
    <Card className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-col gap-3 border-b border-(--color-line) px-5 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={onBack}
            className="rounded px-1 text-(length:--text-small) font-medium text-(--color-navy-700) hover:underline"
          >
            ← {backLabel}
          </button>
          {p && onLoginAs && <LoginAsThisPerson api={api} person={p} actor={actor} onLoginAs={onLoginAs} />}
        </div>
        {p && (
          <div className="flex items-center gap-3.5">
            <Initials name={name!} size="lg" />
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h2 className="text-(length:--text-xl) font-semibold tracking-[-0.015em] text-(--color-ink)">{name}</h2>
                <StatusBadge status={p.status} />
                {warnings.length > 0 && <Badge tone="warn">⚠ {warnings.length} {warnings.length === 1 ? 'warning' : 'warnings'}</Badge>}
              </div>
              <dl className="flex flex-wrap gap-x-5 gap-y-1 text-(length:--text-small)">
                <Meta label="Email" value={p.email ?? 'none in Mindzer'} />
                <Meta label="HR Role" value={p.roleName ? roleLabel(p) : 'No HR Role'} />
                <Meta label="Main company" value={p.compId ? [p.entityName, p.compId].filter(Boolean).join(' · ') : 'none from HR'} />
                <Meta label="Mindzer ID" value={String(p.mindzerUserId)} />
              </dl>
            </div>
          </div>
        )}
        {notice && (
          <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>
            {notice.text}
          </Notice>
        )}
      </div>

      {failure ? (
        <FailureState failure={failure} onRetry={() => setReload((n) => n + 1)} />
      ) : !page || !p ? (
        <Loading label="Reading this user…" />
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto bg-(--color-surface-2) @5xl:grid-cols-[340px_1fr] @5xl:overflow-hidden">
          <div className="flex min-h-0 flex-col gap-4 border-(--color-line) px-4 py-4 @5xl:overflow-y-auto @5xl:border-r">
            <EntryPanel person={p}>
              {!p.systemRole && <SignInLink api={api} person={p} onChanged={saved} />}
            </EntryPanel>
            <Panel title="Approval" aside={<ApprovalTag person={p} />}>
              <ApprovalWords person={p} />
              {canApprove && (
                <Button full variant={warnings.some((w) => w.severity === 'WIDER_ROWS') ? 'danger' : 'primary'} onClick={() => setConfirmApprove(true)} disabled={busy}>
                  {approveLabel(warnings)}
                </Button>
              )}
              {confirmApprove && card?.person && (
                <ConfirmDialog
                  title={`Approve ${name}?`}
                  detail={<ApprovalConsequences card={card} />}
                  confirmLabel={approveLabel(warnings)}
                  tone={warnings.some((w) => w.severity === 'WIDER_ROWS') ? 'danger' : 'default'}
                  busy={busy}
                  onConfirm={approve}
                  onCancel={() => setConfirmApprove(false)}
                />
              )}
              {canWithdraw && !withdrawing && (
                <button
                  type="button"
                  onClick={() => setWithdrawing(true)}
                  className="self-start rounded px-1 text-(length:--text-small) font-medium text-(--color-ink-3) hover:text-(--color-overdue) hover:underline"
                >
                  Take approval back…
                </button>
              )}
              {withdrawing && (
                <div className="flex flex-col gap-2 rounded-md border border-(--color-overdue)/25 bg-(--color-overdue)/5 p-3">
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    placeholder="Reason Of The Change (required, kept with the record and shown on their page)"
                    className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-2 text-(length:--text-small) text-(--color-ink)"
                  />
                  <p className="text-(length:--text-small) text-(--color-ink-3)">
                    {effect === 'entry' ? 'They lose entry at their next request, and wait to be approved again.'
                      : effect === 'system' ? 'Their system role lets them in whatever the approval, so nothing changes now. If the role is removed, they need approving again.'
                        : 'They cannot enter now anyway, so nothing changes today. They need approving again before they can.'}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="danger" onClick={withdraw} disabled={busy || !reason.trim()}>
                      Take approval back
                    </Button>
                    <Button variant="ghost" onClick={() => { setWithdrawing(false); setReason('') }}>
                      Keep it
                    </Button>
                  </div>
                </div>
              )}
            </Panel>
            <Panel title={warnings.length ? `Warnings · ${warnings.length}` : 'Warnings'}>
              <Warnings warnings={warnings} />
            </Panel>
          </div>
          <div className="flex min-h-0 flex-col @5xl:overflow-y-auto">
            <div className="sticky top-0 z-10 border-b border-(--color-line) bg-(--color-surface) px-5 pt-1.5">
              <PageTabs
                label="About this user"
                value={shown}
                onChange={setSection}
                tabs={[
                  { id: 'see' as const, label: 'What they see' },
                  // A system role reads past these settings, so there is nothing to set.
                  ...(p.systemRole ? [] : [
                    // Names: the owner's, 28 Oct 2026 (where each scope comes from: HR, or set in MBI).
                    { id: 'main' as const, label: 'HR main scope' },
                    { id: 'extras' as const, label: `MBI additional scope${page.extras.length ? ` · ${extraScopesOf(page).size}` : ''}` },
                  ]),
                  { id: 'hierarchy' as const, label: 'Hierarchy preview' },
                  { id: 'history' as const, label: 'History' },
                ]}
              />
            </div>
            <div className="flex flex-1 flex-col gap-4 px-5 py-4">
              {shown === 'see' && <WhatTheySee page={page} />}
              {section === 'main' && !p.systemRole && <MainScopeTab api={api} page={page} onSaved={saved} />}
              {section === 'extras' && !p.systemRole && <ExtraScopesTab api={api} page={page} onSaved={saved} />}
              {shown === 'hierarchy' && <HierarchyTab api={api} person={p} onOpenPerson={(other) => onOpenPerson(other, name)} />}
              {shown === 'history' && <HistoryTab api={api} id={p.mindzerUserId} />}
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}

/**
 * "Login As this person" (05 section 7.4), disabled with its reason. It acts as the
 * sign-in linked to them, so a person linked by hand to another address is acted as
 * under that sign-in. Who may act, and as whom (D7, D24), is the server's: it judges
 * when acting begins, and its refusal is shown here.
 */
function LoginAsThisPerson({ api, person: p, actor, onLoginAs }: {
  api: Api
  person: AccessPerson
  actor: string | null
  onLoginAs: (oid: string) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [finding, setFinding] = useState(false)
  const why = loginAsBlocked(p, actor)
  const name = p.displayName ?? `Mindzer user ${p.mindzerUserId}`

  const act = (oid: string) =>
    api
      .beginActAs(oid)
      .then((t) => onLoginAs(t.oid))

  const fail = (e: unknown) => {
    const f = failureOf(e)
    setError('explanation' in f && typeof f.explanation === 'string' ? f.explanation : messageOf(e))
    setBusy(false)
  }

  const go = () => {
    if (why || busy) return
    // Never signed in: their account is found first, after a confirmation (D51).
    if (!p.signInOid) {
      setFinding(true)
      return
    }
    setBusy(true)
    setError(null)
    act(p.signInOid).catch(fail)
  }

  const find = () => {
    setFinding(false)
    setBusy(true)
    setError(null)
    api
      .accessFindAccount(p.mindzerUserId)
      .then((r) => act(r.objectId))
      .catch(fail)
  }

  return (
    // The reason beside the button, on the back line: the heading below stays put.
    <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-1">
      {(why ?? error) && (
        <span className={`text-right text-(length:--text-small) ${why ? 'text-(--color-ink-3)' : 'text-(--color-overdue)'}`}>
          {why ?? error}
        </span>
      )}
      <Button size="sm" onClick={go} disabled={!!why || busy}>
        {busy ? 'Working…' : p.signInOid ? 'Login As this user' : 'Find their account and Login As…'}
      </Button>
      {finding && (
        <ConfirmDialog
          title={`Find ${name}'s account and sign in as them?`}
          tone="default"
          confirmLabel="Find and sign in"
          detail={
            <div className="flex flex-col gap-2">
              <p>
                They have never signed in to MBI. MBI looks up <strong className="font-semibold text-(--color-ink)">{p.email}</strong> in
                Microsoft, records that account (this gives it no access) and links it to {name}, with your name and a reason on
                their history.
              </p>
              <p>
                Then you are signed in as them and see what they would. Until they are approved, that is "Access denied".
              </p>
            </div>
          }
          onConfirm={find}
          onCancel={() => setFinding(false)}
        />
      )}
    </div>
  )
}

/** Why Login As cannot start, in the screen's words; null when it may be tried. */
function loginAsBlocked(p: AccessPerson, actor: string | null): string | null {
  if (actor && p.email && p.email.toLowerCase() === actor.toLowerCase()) return 'This is you.'
  if (p.status === 'PLACEHOLDER') return 'Placeholder account: it never signs in.'
  if (p.status === 'LEFT') return 'Left the company: not an active MBI user.'
  // Their last sign-in can be an unlinked account with their email (a link refused, or taken off).
  if (!p.signInOid && p.lastSignInUtc) return 'No sign-in is linked to them: link one by hand first.'
  // Never signed in: their account is found first (D51), so the button stays.
  if (!p.signInOid && !p.email) return 'No email in Mindzer: their account cannot be found.'
  return null
}

/**
 * "Can they get in?": the answer first, on its own, then every check as its own
 * card, then their phone and hierarchy (owner, 27 Oct 2026: "put each one in a card").
 */
function EntryPanel({ person: p, children }: { person: AccessPerson; children?: ReactNode }) {
  if (p.systemRole) {
    return (
      <Panel title="Can they get in?">
        <Verdict ok tone="privileged" title="Yes: full data access">
          {SYSTEM_ROLE[p.systemRole] ?? p.systemRole}. HR settings do not limit them, and they need no approval.
        </Verdict>
        {children}
      </Panel>
    )
  }
  // Each line follows the server's badge where the badge decides it, so the checks
  // can never disagree with it (review, 26 Oct 2026).
  const noRole = p.status === 'NO_ROLE'
  // Whether the approval counts is the server's flag (17k), not the badge: "role not set
  // up" and the like come first in its order. From a database before 17k, the badges.
  const approved = p.approvalInForce ?? (p.status === 'CAN_ENTER' || p.status === 'NO_SIGN_IN')
  const voided = p.approval === 'APPROVED' && p.approvalInForce === false
  const checks: { ok: boolean | null; text: ReactNode }[] = [
    { ok: p.isActive && p.inSource, text: p.isActive && p.inSource ? 'In Mindzer, active' : 'Left the company in Mindzer' },
    ...(p.isDisabled ? [{ ok: false, text: 'Disabled in Mindzer' }] : []),
    ...(p.status === 'PLACEHOLDER' ? [{ ok: false, text: 'Placeholder or test account: it never signs in' }] : []),
    { ok: p.emailStatus === 'OK', text: p.emailStatus === 'OK' ? 'Company email' : 'No usable company email: fix it in Mindzer' },
    {
      ok: !noRole && p.roleSetUp,
      text: noRole ? 'No HR Role in Mindzer: Mindzer must give them one' : p.roleSetUp ? 'HR Role Configured in MBI' : 'HR Role not configured in MBI: configure it under HR Roles',
    },
    {
      ok: approved,
      text: approved ? 'Approved'
        : p.status === 'RETURNED' || (voided && p.returned) ? 'Approved before they left: look again'
          : p.status === 'WIDENED' || voided ? 'Approved, but an update gave them more since: look again'
            : 'Not approved yet',
    },
    { ok: p.signedIn ? true : null, text: p.signedIn ? `Signed in before${p.lastSignInUtc ? ` (${day(p.lastSignInUtc)})` : ''}` : 'Not signed in to MBI yet' },
  ]
  const now = p.entryNow === 'ALLOWED' ? true : p.entryNow === 'NOT_BUILT' ? null : false
  return (
    <Panel title="Can they get in?">
      <Verdict
        ok={now}
        title={now === true ? 'Yes: they can access the system' : now === null ? 'Not worked out yet' : 'No: they cannot access the system'}
      >
        {now === true ? 'Nothing stops them.'
          : now === null ? 'The next hourly update works it out.'
            : `Reason: ${entryReason(p.entryNow)}.`}
      </Verdict>
      <ul className="flex flex-col gap-1">
        {checks.map((c, i) => (
          <li
            key={i}
            className={`flex items-start gap-2.5 rounded-md border px-3 py-1.5 text-(length:--text-small) ${
              c.ok === true ? 'border-(--color-line) bg-(--color-surface)'
                : c.ok === false ? 'border-(--color-overdue)/25 bg-(--color-overdue)/5 text-(--color-ink)'
                  : 'border-(--color-line) bg-(--color-surface-2)'
            }`}
          >
            <StateDot ok={c.ok} />
            <span className="pt-px text-(--color-ink-2)">{c.text}</span>
          </li>
        ))}
      </ul>
      <div className="grid grid-cols-2 gap-2">
        <Fact label="Phone" value={p.mobileAllowed ? 'Allowed' : 'Not allowed'} from={fromWords(p.mobileFrom)} />
        <Fact label="Hierarchy" value={HIERARCHY[p.hierarchyMode] ?? 'Off'} from={fromWords(p.hierarchyFrom)} />
      </div>
      {children}
    </Panel>
  )
}

/** The approval's state in one word beside the block's title. */
function ApprovalTag({ person: p }: { person: AccessPerson }) {
  if (p.systemRole) return <Badge tone="privileged">Not needed</Badge>
  if (p.status === 'LEFT' || p.status === 'DISABLED' || p.status === 'PLACEHOLDER') return <Badge>Not offered</Badge>
  // An approval that no longer counts under a badge that cannot say so (17k) looks again too.
  if (p.status === 'RETURNED' || p.status === 'WIDENED' || (p.approval === 'APPROVED' && p.approvalInForce === false)) {
    return <Badge tone="warn">Look again</Badge>
  }
  if (p.approval === 'APPROVED') return <Badge tone="good">Approved</Badge>
  if (p.approval === 'WITHDRAWN') return <Badge tone="bad">Taken back</Badge>
  return <Badge tone="warn">Waiting</Badge>
}

function ApprovalWords({ person: p }: { person: AccessPerson }) {
  const text = p.systemRole
    ? 'Not needed: a system role lets them in.'
    : p.status === 'RETURNED'
      ? `Approved ${day(p.decidedUtc)}, but they left and came back since: look again.`
      : p.status === 'WIDENED'
        ? `Approved ${day(p.decidedUtc)}, but an update gave them more than that approval covered: look again.`
        : p.status === 'LEFT' || p.status === 'DISABLED' || p.status === 'PLACEHOLDER'
          ? `Not offered: ${p.status === 'LEFT' ? 'they left the company' : p.status === 'DISABLED' ? 'disabled in Mindzer' : 'a placeholder account never signs in'}.`
      : p.approval === 'APPROVED' && p.approvalInForce === false
        ? `Approved ${day(p.decidedUtc)}, but ${p.returned ? 'they left and came back since' : 'an update gave them more than that approval covered'}: look again.`
      : p.approval === 'APPROVED'
        ? `Approved ${day(p.decidedUtc)}${p.decidedByEmail ? ` by ${p.decidedByEmail}` : ''}${p.decisionWarningCount ? ` · ${p.decisionWarningCount} warnings were showing then` : ''}.`
        : p.approval === 'WITHDRAWN'
          ? `Approval taken back ${day(p.decidedUtc)}${p.decidedByEmail ? ` by ${p.decidedByEmail}` : ''}${p.decisionReason ? `: “${p.decisionReason}”` : ''}. They are waiting again.`
          : `Waiting since ${day(p.firstSeenUtc)} (first seen in Mindzer).`
  // The words above name no approval for these, though one may still count: say it, so
  // the take-back offered under them has something to take back (17k review).
  const standsUnsaid = p.approvalInForce === true
    && (!!p.systemRole || p.status === 'LEFT' || p.status === 'DISABLED' || p.status === 'PLACEHOLDER')
  // About the HR role: only where it is what keeps them out. Approved and in force under
  // "role not set up", setting the role up lets them in; anything undecided or no longer
  // counting still needs an approval. Other badges keep them out for other reasons.
  const roleNote = p.systemRole || p.roleId === null || p.roleSetUp ? null
    : p.status === 'ROLE_NOT_SET' && p.approvalInForce === true ? 'This approval lets them in once their HR Role is configured.'
      : p.approval !== 'APPROVED' || p.approvalInForce === false ? 'Approving does not let them in until their HR Role is configured.'
        : null
  return (
    <div className="flex flex-col gap-1.5 text-(length:--text-small) leading-relaxed text-(--color-ink-2)">
      <p>{text}</p>
      {standsUnsaid && <p>{`Their approval of ${day(p.decidedUtc)}${p.decidedByEmail ? ` by ${p.decidedByEmail}` : ''} still stands.`}</p>}
      {roleNote && <p className="rounded-md bg-(--color-surface-3) px-2.5 py-1.5 text-(--color-ink-3)">{roleNote}</p>}
    </div>
  )
}

const WARNING_LOOK: Record<string, string> = {
  bad: 'border-(--color-overdue)/30',
  warn: 'border-(--color-warn)/30',
  neutral: 'border-(--color-line)',
  good: 'border-(--color-line)',
  privileged: 'border-(--color-line)',
}

/** Every warning in the server's sentence, each on its own card, with its tag and where it comes from. */
export function Warnings({ warnings }: { warnings: AccessWarning[] }) {
  if (warnings.length === 0) {
    return (
      <div className="flex items-center gap-2.5 rounded-md border border-(--color-line) bg-(--color-surface-2) px-3 py-2 text-(length:--text-small) text-(--color-ink-2)">
        <StateDot ok />
        Nothing is wrong with their data.
      </div>
    )
  }
  return (
    <ul className="flex flex-col gap-2">
      {warnings.map((w, i) => (
        <li key={`${w.warningCode}-${i}`} className={`flex flex-col overflow-hidden rounded-md border bg-(--color-surface) ${WARNING_LOOK[(SEVERITY[w.severity] ?? { tone: 'neutral' }).tone]}`}>
          <div className="flex flex-col items-start gap-1.5 px-3 py-2.5">
            <SeverityTag severity={w.severity} />
            <p className="text-(length:--text-small) leading-relaxed text-(--color-ink)">{w.detail}</p>
            <WarningRows json={w.rowsJson} />
          </div>
          {(w.sourceKind || w.sourceRef) && (
            <div className="flex gap-2 border-t border-(--color-line) bg-(--color-surface-2) px-3 py-1.5 text-(length:--text-micro) text-(--color-ink-3)">
              <span className="font-semibold uppercase tracking-[0.06em]">Where</span>
              <span className="min-w-0 break-words">{[w.sourceKind && sourceWords(w.sourceKind), w.sourceRef].filter(Boolean).join(' › ')}</span>
            </div>
          )}
        </li>
      ))}
    </ul>
  )
}

/**
 * What a warning costs, in rows per report (R-25; D20 point 3; owner, 28 Oct 2026):
 * "Not reached in the last 12 months: Receivables 22 · Sales 33", or, for a code D365
 * uses in another company, the rows there. Only the approval card sends it.
 */
function WarningRows({ json }: { json?: string | null }) {
  if (!json) return null
  let read: { Comp_Id: string | null; Reports: { Report_Slug: string; Display_Name: string; Rows: number }[] } | null = null
  try { read = JSON.parse(json) } catch { return null }
  if (!read || !Array.isArray(read.Reports)) return null
  const parts = read.Reports.map((r) => `${r.Display_Name} ${r.Rows.toLocaleString('en-GB')}`)
  if (parts.length === 0) return null
  return (
    <p className="text-(length:--text-small) font-medium text-(--color-ink-2)">
      {read.Comp_Id
        ? <>In {read.Comp_Id}, where D365 uses it, the last 12 months: {parts.join(' · ')} rows they will not see.</>
        : <>Rows this keeps from them, last 12 months: {parts.join(' · ')}.</>}
    </p>
  )
}

function sourceWords(kind: string): string {
  // Where a warning points: Mindzer's own lists and fields (lstUsers.bDisabled), D365, or any MBI
  // setting (approvals, sign-ins, platform roles, overrides): system names, so they stay (review, 2 Nov 2026).
  return { MINDZER: 'Mindzer', D365: 'D365', MBI: 'MBI setting' }[kind] ?? kind
}

/**
 * What they see: the answer in a sentence, their HR Main Scope, then the
 * reports and their column groups side by side, one column per scope (main first,
 * then each extra scope), each cell saying on or off AND where that comes from (7.4).
 * Laid out across the page (owner, 27 Oct 2026: "everything stacked on the left side").
 * A group shows only under a report given somewhere (D53, owner 28 Oct 2026), and a
 * scope that does not give its report shows a dash: it does nothing there.
 */
function WhatTheySee({ page }: { page: AccessPersonPage }) {
  const p = page.person!
  // One column per additional scope: its units and teams are lines sharing the same reports and
  // groups (owner, 29 Oct 2026), so the first line's cells speak for the scope.
  const extraScopes = [...extraScopesOf(page).values()]
  const scopes = [
    { key: 0, title: 'Main', sub: p.compId ?? '—' },
    ...extraScopes.map((rows) => ({
      key: rows[0].extraScopeId,
      // The daily re-check pauses line by line.
      title: rows.every((x) => x.pausedUtc) ? 'Additional · paused' : rows.some((x) => x.pausedUtc) ? 'Additional · partly paused' : 'Additional',
      // Each line as the additional scope card words it (review, 30 Oct 2026).
      sub: `${rows[0].compId} · ${rows.map((x) => (x.anyUnit ? (x.anyTeam ? 'All Business Units · all Business Teams' : `Business Team ${x.businessTeam ?? ''}`)
        : `Business Unit ${x.businessUnit ?? ''} · ${x.anyTeam ? 'all Business Teams' : `Business Team ${x.businessTeam ?? ''}`}`)).join('; ')}`,
    })),
  ]
  const rows = (kind: AccessMatrixCell['kind']) => {
    const codes = [...new Map(page.matrix.filter((c) => c.kind === kind).map((c) => [c.code, c])).values()]
    return codes.map((c) => ({
      code: c.code,
      label: c.label,
      reportSlug: c.reportSlug,
      cells: scopes.map((s) => page.matrix.find((x) => x.kind === kind && x.code === c.code && x.scopeKey === s.key) ?? null),
    }))
  }
  // A paused extra scope, or one in their own main company, gives nothing (the builder leaves both out);
  // a partly paused one gives through its lines still in use.
  const live = scopes.map((s) => {
    const rows = extraScopes.find((r) => r[0].extraScopeId === s.key)
    return s.key === 0 || (!!rows && rows.some((x) => !x.pausedUtc) && rows[0].compId !== p.compId)
  })
  const reports = rows('REPORT')
  const opens = reports.filter((r) => r.cells.some((c, i) => c?.value && live[i])).map((r) => r.label)
  // Whether a scope gives a report, the condition for any of its groups to count there.
  const reportOn = (slug: string, i: number) => !!reports.find((r) => r.code === slug)?.cells[i]?.value
  const reportName = (slug: string) => reports.find((r) => r.code === slug)?.label ?? slug
  const groups = rows('GROUP')
    .filter((g) => scopes.some((_, i) => reportOn(g.reportSlug, i)))
    .sort((a, b) => reportName(a.reportSlug).localeCompare(reportName(b.reportSlug)) || a.label.localeCompare(b.label))
  const canEnter = p.entryNow === 'ALLOWED' || !!p.systemRole

  return (
    <>
      <Verdict
        ok={p.systemRole || opens.length > 0 ? (canEnter ? true : null) : false}
        tone={p.systemRole ? 'privileged' : undefined}
        title={p.systemRole ? 'Every report and every column'
          : opens.length === 0 ? 'No report access' : `${opens.length} ${opens.length === 1 ? 'report' : 'reports'}${canEnter ? '' : ', once they can enter'}`}
      >
        {p.systemRole
          ? 'A system role reads past these settings.'
          : opens.length === 0
            ? 'Nothing here gives them report access.'
            : `${canEnter ? 'They can open' : 'Once they can enter, they will open'} ${listWords(opens)}.`}
      </Verdict>

      {/* The main company in dark text: it decides what they see (owner, 28 Oct 2026). Named as on the
          HR main scope tab (owner, 2 Nov 2026). */}
      <Panel title="HR Main Scope" aside={<span className="text-(length:--text-small) font-medium text-(--color-ink)">{p.compId ? `Main company ${[p.entityName, p.compId].filter(Boolean).join(' · ')}` : 'No main company'}</span>}>
        {page.placement.length === 0 ? (
          <p className="text-(length:--text-small) text-(--color-ink-3)">No Business Unit or Business Team from HR. They will see no rows.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {(['UNIT', 'TEAM'] as const).map((kind) => (
              <div key={kind} className="flex flex-wrap items-center gap-2">
                <span className="eyebrow w-32">{kind === 'UNIT' ? 'Business Units' : 'Business Teams'}</span>
                {page.placement.filter((x) => x.kind === kind).map((x) => <PlaceChip key={`${x.mindzerId}-${x.d365Code ?? ''}`} name={x.name ?? `#${x.mindzerId}`} code={x.d365Code} />)}
                {page.placement.every((x) => x.kind !== kind) && <span className="text-(length:--text-small) text-(--color-ink-4)">none</span>}
              </div>
            ))}
          </div>
        )}
      </Panel>

      <div className="grid grid-cols-1 gap-4 @5xl:grid-cols-2">
        {(['REPORT', 'GROUP'] as const).map((kind) => (
          <Panel key={kind} title={kind === 'REPORT' ? 'Reports' : 'Column groups'} bodyClassName="!px-0 !py-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-(length:--text-small)">
                <thead>
                  <tr className="bg-(--color-surface-2) text-left">
                    <th className="px-4 py-2 font-medium text-(--color-ink-3)">{kind === 'REPORT' ? 'Report' : 'Report · column group'}</th>
                    {scopes.map((s, i) => (
                      <th key={s.key} className="whitespace-nowrap px-4 py-2 font-medium">
                        <div className="text-(length:--text-micro) font-semibold uppercase tracking-[0.06em] text-(--color-ink-3)">{s.title}</div>
                        <div className="font-normal text-(--color-ink-2)">{s.sub}</div>
                        {/* Said in words, not by fading the column: faded, "On" fell below readable contrast. */}
                        {!live[i] && <div className="font-normal text-(--color-warn)">gives nothing now</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kind === 'GROUP' && groups.length === 0 && (
                    <tr className="border-t border-(--color-line)">
                      <td colSpan={scopes.length + 1} className="px-4 py-3 text-(--color-ink-3)">
                        {opens.length === 0 ? 'No report given, so no column group applies.' : 'The reports they open have no column groups yet.'}
                      </td>
                    </tr>
                  )}
                  {(kind === 'REPORT' ? reports : groups).map((r) => (
                    <tr key={r.code} className="border-t border-(--color-line) transition-colors hover:bg-(--color-navy-50)">
                      <td className="whitespace-nowrap px-4 py-2 font-medium text-(--color-ink)">
                        {kind === 'GROUP' && <span className="font-normal text-(--color-ink-3)">{reportName(r.reportSlug)} · </span>}
                        {r.label}
                      </td>
                      {r.cells.map((c, i) => (
                        <td key={i} className="whitespace-nowrap px-4 py-2">
                          {c && (kind === 'REPORT' || reportOn(r.reportSlug, i))
                            ? <SourcePill cell={c} />
                            : <span className="text-(--color-ink-4)" title={kind === 'GROUP' ? 'This scope does not give the report.' : undefined}>—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        ))}
      </div>
    </>
  )
}

/** One cell of what they see: on or off, then where that comes from. */
function SourcePill({ cell }: { cell: AccessMatrixCell }) {
  const [state, from, look] = {
    ROLE: ['On', 'HR Role', 'bg-(--color-good)/10 text-(--color-good)'],
    ROLE_OFF: ['Off', 'HR Role', 'bg-(--color-surface-3) text-(--color-ink-3)'],
    PERSON_ON: ['On', 'set for this user', 'bg-(--color-navy-100) text-(--color-navy-700)'],
    PERSON_OFF: ['Off', 'set for this user', 'bg-(--color-overdue)/10 text-(--color-overdue)'],
    GIVEN: ['On', 'given here', 'bg-(--color-good)/10 text-(--color-good)'],
    NOT_GIVEN: ['Off', 'not given', 'bg-(--color-surface-3) text-(--color-ink-3)'],
  }[cell.source]
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`inline-flex h-5 min-w-9 items-center justify-center rounded-full px-2 text-(length:--text-micro) font-semibold ${look}`}>{state}</span>
      <span className="text-(length:--text-micro) text-(--color-ink-3)">{from}</span>
    </span>
  )
}

function approvalWords(status: AccessPersonStatus, approval: string | null, decidedUtc: string | null, inForce?: boolean): ReactNode {
  if (status === 'SEES_ALL') return <span className="text-(--color-ink-4)">Not needed</span>
  // An approval older than their return or a freeze no longer counts (17k: as their page says).
  if (approval === 'APPROVED' && inForce === false) return <span className="text-(--color-warn)">Look again (approved {day(decidedUtc)})</span>
  if (approval === 'APPROVED') return `Approved ${day(decidedUtc)}`
  if (approval === 'WITHDRAWN') return `Approval taken back ${day(decidedUtc)}`
  return <span className="text-(--color-ink-3)">Waiting</span>
}

/**
 * Who Approve is offered to: everyone undecided (sec.v_Approval_Waiting_MBI), and
 * anyone whose approval no longer counts (back after leaving, gained more). Not
 * leavers, the disabled, placeholders, people with no usable email (51622) or a
 * system role.
 */
const APPROVABLE: AccessPersonStatus[] = ['WAITING', 'RETURNED', 'WIDENED', 'ROLE_NOT_SET', 'NO_ROLE']

function fromWords(from: string): string {
  return from === 'PERSON' ? 'their own override' : from === 'ROLE' ? 'from the HR Role' : 'no HR Role configured'
}
